import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AuthUser } from 'aws-amplify/auth';
import { PickResult } from '../types';
import { getMyPicks } from '../api';
import './MyPicks.css';

interface Props { user: AuthUser; }

type Filter = 'all' | 'pending' | 'scored' | 'correct' | 'incorrect';

function Flag({ src, name }: { src: string; name: string }) {
  const [failed, setFailed] = useState(!src);
  if (failed) return <span className="mpf-abbr">{name.slice(0, 3).toUpperCase()}</span>;
  return <img src={src} alt={name} className="mpf-img" onError={() => setFailed(true)} />;
}

function modeLabel(pick: PickResult) {
  if (!pick.revealed) return { icon: '🧠', label: 'Blind' };
  if (pick.challenged) return { icon: '⚔️', label: 'Challenge' };
  return { icon: '✓', label: 'Follow' };
}

function pickLabel(pick: PickResult) {
  if (pick.pick === 'home') return `🏠 ${pick.homeTeam} Win`;
  if (pick.pick === 'away') return `${pick.awayTeam} Win ✈`;
  return '= Draw';
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function groupKey(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

export default function MyPicks({ user: _user }: Props) {
  const [picks, setPicks] = useState<PickResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('all');

  useEffect(() => {
    getMyPicks().then(data => { setPicks(data); setLoading(false); });
  }, []);

  const totalPoints = picks.reduce((s, p) => s + p.points, 0);
  const correct   = picks.filter(p => p.isCorrect === true).length;
  const incorrect = picks.filter(p => p.isCorrect === false).length;
  const scored    = picks.filter(p => p.status === 'scored').length;
  const pending   = picks.filter(p => p.status === 'pending').length;

  const userAcc   = scored > 0 ? Math.round((correct / scored) * 100) : null;
  const aiCorrect = picks.filter(p =>
    p.status === 'scored' && p.aiChoice && p.outcome && p.aiChoice === p.outcome
  ).length;
  const aiAcc     = scored > 0 ? Math.round((aiCorrect / scored) * 100) : null;
  const beatingAI = userAcc !== null && aiAcc !== null && userAcc > aiAcc;

  const filtered = useMemo(() => {
    const base =
      filter === 'pending'   ? picks.filter(p => p.status === 'pending')
    : filter === 'scored'    ? picks.filter(p => p.status === 'scored')
    : filter === 'correct'   ? picks.filter(p => p.isCorrect === true)
    : filter === 'incorrect' ? picks.filter(p => p.isCorrect === false)
    : picks;
    // Pending first (by kickoff asc), then scored (by kickoff desc)
    return [...base].sort((a, b) => {
      if (a.status !== b.status) return a.status === 'pending' ? -1 : 1;
      return a.status === 'pending'
        ? a.kickoffTime.localeCompare(b.kickoffTime)
        : b.kickoffTime.localeCompare(a.kickoffTime);
    });
  }, [picks, filter]);

  const groups = useMemo(() => {
    const map = new Map<string, PickResult[]>();
    for (const p of filtered) {
      const key = groupKey(p.kickoffTime);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    }
    return Array.from(map.entries());
  }, [filtered]);

  if (loading) {
    return (
      <div className="mypicks-page container">
        <div className="loading-state"><div className="spinner" /><p>Loading…</p></div>
      </div>
    );
  }

  return (
    <div className="mypicks-page container">
      <div className="matches-header">
        <h1 className="page-title">My Picks</h1>
        <p className="page-sub">
          {picks.length === 0 ? 'No predictions yet'
            : `${picks.length} picks · ${scored} scored · ${totalPoints >= 0 ? '+' : ''}${totalPoints} pts`}
        </p>
      </div>

      {picks.length > 0 && (
        <>
          {/* ── Stats bar (clickable) ── */}
          <div className="mp-stats-row">
            <button
              className={`mp-stat mp-stat-btn${filter === 'all' ? ' mp-stat--active' : ''}`}
              onClick={() => setFilter('all')}
            >
              <span className="mp-stat-val">{picks.length}</span>
              <span className="mp-stat-label">Total</span>
            </button>
            <button
              className={`mp-stat mp-stat-btn${filter === 'correct' ? ' mp-stat--active' : ''}`}
              onClick={() => setFilter('correct')}
            >
              <span className="mp-stat-val">{correct}</span>
              <span className="mp-stat-label">Correct</span>
            </button>
            <div className="mp-stat">
              <span className="mp-stat-val">
                {userAcc !== null ? `${userAcc}%` : '—'}
              </span>
              <span className="mp-stat-label">Accuracy</span>
              {userAcc !== null && aiAcc !== null && (
                <span className={`mp-ai-vs ${beatingAI ? 'mp-ai-win' : userAcc === aiAcc ? 'mp-ai-tied' : 'mp-ai-loss'}`}>
                  {beatingAI ? '↑' : userAcc === aiAcc ? '=' : '↓'} AI {aiAcc}%
                </span>
              )}
            </div>
            <div className="mp-stat mp-stat-pts">
              <span className="mp-stat-val">{totalPoints >= 0 ? `+${totalPoints}` : totalPoints}</span>
              <span className="mp-stat-label">Points</span>
            </div>
          </div>

          {/* ── Filter tabs ── */}
          <div className="mp-tabs">
            {(['all', 'pending', 'scored', 'correct', 'incorrect'] as Filter[]).map(f => (
              <button
                key={f}
                className={`mp-tab${filter === f ? ' mp-tab--active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f === 'all'       ? `All (${picks.length})`
                 : f === 'pending'   ? `⏳ Pending (${pending})`
                 : f === 'scored'    ? `🏁 Results (${scored})`
                 : f === 'correct'   ? `✓ Correct (${correct})`
                 : `✗ Wrong (${incorrect})`}
              </button>
            ))}
          </div>
        </>
      )}

      {filtered.length === 0 ? (
        <div className="mp-empty">
          <div className="mp-empty-icon">
            {filter === 'pending' ? '⏳' : filter === 'correct' ? '✓' : filter === 'incorrect' ? '✗' : filter === 'scored' ? '🏁' : '🎯'}
          </div>
          <h2 className="mp-empty-title">
            {filter === 'pending'   ? 'No pending picks'
             : filter === 'scored'    ? 'No results yet'
             : filter === 'correct'   ? 'No correct picks yet'
             : filter === 'incorrect' ? 'No wrong picks'
             : 'No picks yet'}
          </h2>
          <p className="mp-empty-sub">
            {picks.length === 0 ? 'Head to Matches and place your predictions before kickoff.' : 'Try a different filter above.'}
          </p>
          {picks.length === 0 && <Link to="/matches" className="btn-primary">Browse Matches →</Link>}
        </div>
      ) : (
        <div className="mp-groups">
          {groups.map(([date, datePicks]) => (
            <div key={date} className="mp-group">
              <div className="mp-group-header">{date}</div>
              <div className="mp-grid">
                {datePicks.map(pick => {
                  const mode = modeLabel(pick);
                  const isCorrect = pick.isCorrect;
                  const cardClass = [
                    'mp-card',
                    isCorrect === true ? 'mp-card--correct' : isCorrect === false ? 'mp-card--wrong' : '',
                  ].filter(Boolean).join(' ');

                  return (
                    <div key={pick.matchId} className={cardClass}>
                      {/* Competition */}
                      <div className="mp-card-comp">{pick.competition}</div>

                      {/* Teams row */}
                      <div className="mp-teams-row">
                        <div className="mp-team">
                          <Flag src={pick.homeTeamLogo} name={pick.homeTeam} />
                          <span className="mp-team-name">{pick.homeTeam}</span>
                        </div>

                        <div className="mp-score-vs">
                          {pick.score ? (
                            <span className="mp-final-score">
                              {pick.score.home} – {pick.score.away}
                            </span>
                          ) : (
                            <span className="mp-vs">VS</span>
                          )}
                        </div>

                        <div className="mp-team mp-team--away">
                          <span className="mp-team-name">{pick.awayTeam}</span>
                          <Flag src={pick.awayTeamLogo} name={pick.awayTeam} />
                        </div>
                      </div>

                      {/* Pick + mode */}
                      <div className="mp-pick-row">
                        <span className="mp-pick-badge">
                          {pickLabel(pick)}
                        </span>
                        <span className="mp-mode-badge">
                          {mode.icon} {mode.label}
                        </span>
                      </div>

                      {/* Status footer */}
                      <div className="mp-footer-row">
                        {pick.status === 'scored' ? (
                          <span className={`mp-result-badge ${isCorrect ? 'mp-result--correct' : 'mp-result--wrong'}`}>
                            {isCorrect
                              ? `✓ Correct · +${pick.points} pts`
                              : `✗ Wrong · ${pick.points} pts`}
                          </span>
                        ) : (
                          <span className="mp-result-badge mp-result--pending">⏳ Pending</span>
                        )}
                        <span className="mp-kickoff-time">{formatTime(pick.kickoffTime)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {picks.length > 0 && (
        <div className="mp-browse-link">
          <Link to="/matches" className="btn-secondary">Browse More Matches →</Link>
        </div>
      )}
    </div>
  );
}
